/**
 * Freemium Analytics Tracking Utility
 * Tracks conversion events and user behavior for freemium monetization
 */

import { buildApiUrl } from '../config/api.config';

interface ConversionFunnel {
  signalLimitHits: number;
  upgradePromptShows: number;
  upgradePromptClicks: number;
  conversionRate: number;
  topTriggers: { trigger: string; count: number }[];
}

interface ConversionEvent {
  event: string;
  userId?: string;
  userTier: 'FREE' | 'PROFESSIONAL' | 'ENTERPRISE';
  timestamp: string;
  metadata?: Record<string, unknown>;
}

interface FreemiumMetrics {
  signalLimitHits: number;
  confidenceLockClicks: number;
  chartLockClicks: number;
  upgradePromptShows: number;
  upgradePromptClicks: number;
  upgradePromptDismissals: number;
  confidenceReveals: number;
  featureLockInteractions: number;
}

class FreemiumAnalyticsService {
  private events: ConversionEvent[] = [];
  private metrics: FreemiumMetrics = {
    signalLimitHits: 0,
    confidenceLockClicks: 0,
    chartLockClicks: 0,
    upgradePromptShows: 0,
    upgradePromptClicks: 0,
    upgradePromptDismissals: 0,
    confidenceReveals: 0,
    featureLockInteractions: 0
  };

  /**
   * Track a freemium conversion event
   */
  trackEvent(
    event: string, 
    userTier: 'FREE' | 'PROFESSIONAL' | 'ENTERPRISE',
    metadata?: Record<string, unknown>,
    userId?: string
  ): void {
    const conversionEvent: ConversionEvent = {
      event,
      userId,
      userTier,
      timestamp: new Date().toISOString(),
      metadata
    };

    this.events.push(conversionEvent);
    this.updateMetrics(event);
    
    // Store in localStorage for persistence
    try {
      const storedEvents = localStorage.getItem('freemium_events') ?? '[]';
      const events = JSON.parse(storedEvents) as ConversionEvent[];
      events.push(conversionEvent);

      // Keep only events from last 24 hours and max 100
      const dayAgo = Date.now() - 24 * 60 * 60 * 1000;
      const cleaned = events
        .filter((e: ConversionEvent) => new Date(e.timestamp).getTime() >= dayAgo)
        .slice(-100);

      localStorage.setItem('freemium_events', JSON.stringify(cleaned));
    } catch (error) {
      console.warn('Failed to store freemium event:', error);
    }

    // Send to backend analytics (if available)
    void this.sendToBackend(conversionEvent);
    
    // console.log('[FreemiumAnalytics]', event, metadata);
  }

  /**
   * Track signal limit hit (key conversion moment)
   */
  trackSignalLimit(signalsUsed: number, maxSignals: number, userId?: string): void {
    this.trackEvent('free_signal_limit_hit', 'FREE', {
      signalsUsed,
      maxSignals,
      conversionOpportunity: 'high',
      touchpoint: 'signal_access'
    }, userId);
  }

  /**
   * Track confidence score interaction
   */
  trackConfidenceInteraction(
    action: 'locked_click' | 'reveal' | 'delay_shown',
    signalId: string,
    userTier: 'FREE' | 'PROFESSIONAL' | 'ENTERPRISE',
    userId?: string
  ): void {
    this.trackEvent(`confidence_${action}`, userTier, {
      signalId,
      feature: 'confidence_score',
      conversionOpportunity: action === 'locked_click' ? 'high' : 'medium'
    }, userId);
  }

  /**
   * Track probability charts interaction
   */
  trackChartsInteraction(
    action: 'locked_click' | 'view' | 'analysis_request',
    signalId: string,
    userTier: 'FREE' | 'PROFESSIONAL' | 'ENTERPRISE',
    userId?: string
  ): void {
    this.trackEvent(`charts_${action}`, userTier, {
      signalId,
      feature: 'probability_charts',
      conversionOpportunity: action === 'locked_click' ? 'high' : 'low'
    }, userId);
  }

  /**
   * Track upgrade prompt interactions
   */
  trackUpgradePrompt(
    action: 'shown' | 'clicked' | 'dismissed',
    trigger: 'signal_limit' | 'confidence_delay' | 'feature_locked' | 'chart_locked',
    userId?: string
  ): void {
    this.trackEvent(`upgrade_prompt_${action}`, 'FREE', {
      trigger,
      conversionFunnel: 'upgrade_prompt',
      conversionOpportunity: (() => {
        if (action === 'clicked') {return 'very_high';}
        if (action === 'shown') {return 'medium';}
        return 'low';
      })()
    }, userId);
  }

  /**
   * Track feature usage patterns
   */
  trackFeatureUsage(
    feature: string,
    userTier: 'FREE' | 'PROFESSIONAL' | 'ENTERPRISE',
    usage: 'attempted' | 'successful' | 'blocked',
    userId?: string
  ): void {
    this.trackEvent('feature_usage', userTier, {
      feature,
      usage,
      conversionOpportunity: usage === 'blocked' ? 'high' : 'low'
    }, userId);
  }

  /**
   * Get conversion funnel analysis
   */
  getConversionFunnel(): {
    signalLimitHits: number;
    upgradePromptShows: number;
    upgradePromptClicks: number;
    conversionRate: number;
    topTriggers: { trigger: string; count: number }[];
  } {
    const signalLimitEvents = this.events.filter(e => e.event === 'free_signal_limit_hit');
    const promptShownEvents = this.events.filter(e => e.event === 'upgrade_prompt_shown');
    const promptClickedEvents = this.events.filter(e => e.event === 'upgrade_prompt_clicked');
    
    const conversionRate = promptShownEvents.length > 0 
      ? (promptClickedEvents.length / promptShownEvents.length) * 100 
      : 0;

    // Analyze top triggers
    const triggerCounts = new Map<string, number>();
    promptShownEvents.forEach(event => {
      const trigger = (event.metadata?.trigger as string) ?? 'unknown';
      triggerCounts.set(trigger, (triggerCounts.get(trigger) ?? 0) + 1);
    });

    const topTriggers = Array.from(triggerCounts.entries())
      .map(([trigger, count]) => ({ trigger, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    return {
      signalLimitHits: signalLimitEvents.length,
      upgradePromptShows: promptShownEvents.length,
      upgradePromptClicks: promptClickedEvents.length,
      conversionRate: Number(conversionRate.toFixed(2)),
      topTriggers
    };
  }

  /**
   * Get user engagement patterns
   */
  getUserEngagement(userId?: string): {
    totalEvents: number;
    uniqueFeatures: number;
    conversionEvents: number;
    lastActivity: string | null;
    engagementScore: number;
  } {
    const userEvents = userId 
      ? this.events.filter(e => e.userId === userId)
      : this.events;

    const uniqueFeatures = new Set(
      userEvents.map(e => e.metadata?.feature).filter(Boolean)
    ).size;

    const conversionEvents = userEvents.filter(e => 
      e.event.includes('upgrade_') || 
      e.event.includes('limit_hit') || 
      e.event.includes('locked_click')
    ).length;

    const lastActivity = userEvents.length > 0 
      ? userEvents[userEvents.length - 1].timestamp 
      : null;

    // Simple engagement score: 0-100 based on event variety and frequency
    const engagementScore = Math.min(100, 
      (uniqueFeatures * 10) + 
      (conversionEvents * 5) + 
      Math.min(userEvents.length, 20)
    );

    return {
      totalEvents: userEvents.length,
      uniqueFeatures,
      conversionEvents,
      lastActivity,
      engagementScore
    };
  }

  /**
   * Get current metrics snapshot
   */
  getMetrics(): FreemiumMetrics {
    return { ...this.metrics };
  }

  /**
   * Export analytics data for reporting
   */
  exportAnalytics(): {
    events: ConversionEvent[];
    metrics: FreemiumMetrics;
    conversionFunnel: ConversionFunnel;
    timeRange: { start: string; end: string };
  } {
    const now = new Date().toISOString();
    const dayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    
    return {
      events: this.events.filter(e => e.timestamp >= dayAgo),
      metrics: this.getMetrics(),
      conversionFunnel: this.getConversionFunnel(),
      timeRange: {
        start: dayAgo,
        end: now
      }
    };
  }

  private updateMetrics(event: string): void {
    switch (event) {
      case 'free_signal_limit_hit':
        this.metrics.signalLimitHits++;
        break;
      case 'confidence_locked_click':
        this.metrics.confidenceLockClicks++;
        break;
      case 'charts_locked_click':
        this.metrics.chartLockClicks++;
        break;
      case 'upgrade_prompt_shown':
        this.metrics.upgradePromptShows++;
        break;
      case 'upgrade_prompt_clicked':
        this.metrics.upgradePromptClicks++;
        break;
      case 'upgrade_prompt_dismissed':
        this.metrics.upgradePromptDismissals++;
        break;
      case 'confidence_reveal':
        this.metrics.confidenceReveals++;
        break;
      default:
        if (event.includes('_locked_') || event.includes('feature_')) {
          this.metrics.featureLockInteractions++;
        }
        break;
    }
  }

  private async sendToBackend(event: ConversionEvent): Promise<void> {
    try {
      // Only send conversion-critical events to reduce API load
      const criticalEvents = [
        'free_signal_limit_hit',
        'upgrade_prompt_shown',
        'upgrade_prompt_clicked',
        'confidence_locked_click',
        'charts_locked_click'
      ];

      if (!criticalEvents.includes(event.event)) {
        return;
      }

      const response = await fetch(buildApiUrl('/api/analytics/freemium'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(event)
      });

      if (!response.ok) {
        throw new Error(`Analytics API error: ${response.status}`);
      }
    } catch (error) {
      // Fail silently for analytics - don't break user experience
      console.warn('[FreemiumAnalytics] Failed to send to backend:', error);
    }
  }

  /**
   * Load stored events from localStorage
   */
  loadStoredEvents(): void {
    try {
      const storedEvents = localStorage.getItem('freemium_events');
      if (storedEvents) {
        const events = JSON.parse(storedEvents) as ConversionEvent[];
        this.events = events.filter((e: ConversionEvent) => {
          // Only keep events from last 24 hours
          const eventTime = new Date(e.timestamp).getTime();
          const dayAgo = Date.now() - 24 * 60 * 60 * 1000;
          return eventTime >= dayAgo;
        });

        // Rebuild metrics from loaded events
        this.metrics = {
          signalLimitHits: 0,
          confidenceLockClicks: 0,
          chartLockClicks: 0,
          upgradePromptShows: 0,
          upgradePromptClicks: 0,
          upgradePromptDismissals: 0,
          confidenceReveals: 0,
          featureLockInteractions: 0
        };

        this.events.forEach(event => this.updateMetrics(event.event));
      }
    } catch (error) {
      console.warn('Failed to load stored freemium events:', error);
    }
  }
}

// Export singleton instance
export const freemiumAnalytics = new FreemiumAnalyticsService();

// Initialize on load
freemiumAnalytics.loadStoredEvents();

export default freemiumAnalytics;